import { FC, useEffect, useState } from 'react';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import GlobalSearch from 'src/components/GlobalSearch';
import CompanySelector from 'src/components/CompanySelector';
import BellNotification from 'src/components/BellNotification';
import ENVIRONMENT from 'src/utils/environments';
import useWebSocket from 'src/utils/useWebSocket';
import { useAppTour } from 'src/context/appTourContext';
import NavbarMenu from './NavbarMenu';
import NewRequestButton from '../NewRequestButton';

interface NavBarProps {
  onInvite: () => void;
  onResetPassword: () => void;
}

const Navbar: FC<NavBarProps> = ({ onInvite, onResetPassword }) => {
  const [newUpdate, setNewUpdate] = useState(false);

  const [openMenu, setOpenMenu] = useState(false);
  const [openedTour, setOpenedTour] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const breakpoints = useBreakpoint(true);
  const { setOpenTour, setStepTarget, unsetStepTarget, subscribe } =
    useAppTour();

  useEffect(() => {
    return subscribe((current) => {
      if (current === null) {
        setOpenedTour(false);
        return;
      }

      setOpenedTour(true);

      if (
        [4, 5].includes(current) ||
        (!breakpoints.md && current === 0) ||
        (!breakpoints.xl && [2, 3].includes(current))
      ) {
        setOpenMenu(true);
        return;
      }

      setOpenMenu(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpoints.md, breakpoints.xl]);

  useEffect(() => {
    if (!openMenu) {
      unsetStepTarget(4);
      unsetStepTarget(5);

      if (!breakpoints.md) {
        unsetStepTarget(0);
      }

      if (!breakpoints.xl) {
        unsetStepTarget(2);
        unsetStepTarget(3);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMenu, breakpoints.md, breakpoints.xl]);

  useWebSocket({
    channelName: 'events',
    privateChannel: false,
    listen: {
      event: '.version.update',
      callback: (event) => {
        if (event.env === 'client') {
          setNewUpdate(true);
        }
      },
    },
  });

  return (
    <div className="sticky left-0 top-0 z-10 mt-0 w-full bg-[#0044CC] pt-0 shadow-sm">
      {newUpdate && (
        <div className="bg-[#69e35b] p-2 text-center text-sm">
          A new update is available. Please{' '}
          <span
            className="cursor-pointer text-[#0044cc]"
            onClick={() => window.location.reload()}
          >
            refresh
          </span>{' '}
          your browser.
        </div>
      )}

      {ENVIRONMENT.IS_PRODUCTION ? undefined : (
        <div className="bg-orange-400 py-1 text-center text-sm text-white">
          Development
        </div>
      )}

      <div className="mx-auto flex h-16 w-full max-w-[1920px] items-center gap-4 px-4 sm:px-6 lg:px-8">
        <Link
          className="flex max-w-[90px] flex-none items-center justify-center text-3xl leading-none text-primary md:max-w-[120px]"
          to="/dashboard"
        >
          <img src="/lionshare.svg" alt="Lionshare" />
        </Link>

        <div className="flex flex-1 items-center justify-end gap-4">
          {breakpoints.md && (
            <div
              className="w-full max-w-[520px]"
              ref={(node) => setStepTarget(0, node)}
            >
              <GlobalSearch dropdownRenderClassName="min-w-[360px] w-[calc(100%+120px)]" />
            </div>
          )}

          <NewRequestButton onClick={() => setOpenMenu(false)} />

          {breakpoints.md && (
            <CompanySelector
              className="min-w-[105px] max-w-[280px]"
              customerLabel="business_name"
            />
          )}

          <div ref={(node) => setStepTarget(1, node)}>
            <BellNotification />
          </div>

          <NavbarMenu
            open={openMenu}
            breakpoints={breakpoints}
            onOpenChange={(value) => {
              if (!openedTour) {
                setOpenMenu(value);
              }
            }}
            onInvite={onInvite}
            onResetPassword={onResetPassword}
            onStartTour={() => {
              if (location.pathname !== '/dashboard') {
                navigate('/dashboard');
              }
              setOpenTour(true);
            }}
            setStepTarget={setStepTarget}
          />
        </div>
      </div>
    </div>
  );
};

export default Navbar;
